<template>
  <div class="supergroup-list container-fluid">
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <h2 class="h3">Supergroups</h2>
          <button
            @click="showCreateModal = true"
            class="btn btn-primary"
            :disabled="!canManageSupergroups"
          >
            <i class="fa fa-plus-circle me-1"></i> Create Supergroup
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="row">
      <div class="col text-center py-5">
        <div class="spinner-border text-primary">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading supergroups...</p>
      </div>
    </div>

    <div v-else-if="error" class="row">
      <div class="col">
        <div class="alert alert-danger" role="alert">
          <i class="fa fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
      </div>
    </div>

    <div v-else-if="!rootSupergroups.length" class="row">
      <div class="col">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fa fa-folder display-4 text-muted mb-3"></i>
            <p class="text-muted">
              No supergroups found. Create your first supergroup to get started.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div :class="{'col-md-6': isUserPanelOpen, 'col-md-12': !isUserPanelOpen}">
        <div class="card">
          <div class="card-body">
            <SupergroupListItem
              ref="supergroupListItems"
              :itemClass="index % 2 === 1 ? 'odd' : 'even'"
              v-for="(supergroup, index) in rootSupergroups"
              :key="supergroup.uuid"
              :supergroup="supergroup"
              :all-supergroups="supergroups"
              @edit="editSupergroup"
              @delete="confirmDelete"
                @select="selectSupergroup"
              :canManageSupergroups="canManageSupergroups"
            />
          </div>
        </div>
      </div>
        <div class="col-md-6" v-if="isUserPanelOpen && selectedSupergroup">
          <supergroup-users 
            :supergroup="selectedSupergroup"
            @close="closeUserPanel"
          ></supergroup-users>
        </div>
    </div>

    <!-- Create/Edit Modal -->
    <SupergroupForm
      v-if="(showCreateModal || showEditModal) && canManageSupergroups"
      :selectedSupergroup="currentSupergroup"
      :allSupergroups="supergroups"
      :is-edit="showEditModal"
      @close="closeModal"
      @save="saveSupergroup"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-backdrop">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="showDeleteModal = false" aria-label="Close">
              <i class="fa fa-times"></i>
            </button>
            <h5 class="modal-title">Confirm Delete</h5>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete the supergroup "{{ currentSupergroup.name }}"?</p>
            <div v-if="hasChildren" class="alert alert-warning mt-3">
              <i class="fa fa-exclamation-triangle me-2"></i>
              Warning: This supergroup has child supergroups that will also be deleted.
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showDeleteModal = false" class="btn btn-secondary">
              Cancel
            </button>
            <button @click="deleteSupergroup" class="btn btn-danger">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SupergroupListItem from './SupergroupListItem.vue'
import SupergroupForm from './SupergroupForm.vue'
import SupergroupUsers from './SupergroupUsers.vue';
import { Permissions } from '@/client_auth/permissions'

export default {
  name: 'SupergroupList',
  components: {
    SupergroupListItem,
    SupergroupForm,
    SupergroupUsers
  },
  data() {
    return {
      supergroups: [],
      loading: true,
      error: null,
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      currentSupergroup: null,
      selectedSupergroup: null,
      isUserPanelOpen: false,
      canManageSupergroups: false
    }
  },
  computed: {
    rootSupergroups() {
      return this.supergroups
        .filter(sg => !sg.parent_id)
        .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    },
    hasChildren() {
      return !!(this.currentSupergroup && this.currentSupergroup.child_count > 0)
    }
  },
  async mounted() {
    await this.fetchSupergroups()
    await this.$angularWorld.auth.getCurrentUser()
    await this.$angularWorld.auth
      .doesUserHavePermission([Permissions.UserOps])
      .then(hasPermission => {
        this.canManageSupergroups = hasPermission
      })
    // Wait for the initial render of the list before trying to find components
    await this.$nextTick()

    const hashParts = window.location.hash.split('#')
    const hash = hashParts.length > 1 ? hashParts[hashParts.length - 1] : null

    if (hash) {
      const ancestorUuids = this.findSupergroupAndItsAncestors(hash)

      for (const uuid of ancestorUuids) {
        const component = this.findComponentByUuid(this.$refs.supergroupListItems, uuid)
        if (component) {
          component.expand()
          // Wait for the DOM to update after expanding, so children are rendered
          await this.$nextTick()
        }
      }

      const finalElement = document.getElementById(hash)
      if (finalElement) {
        finalElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  },
  methods: {
    async fetchSupergroups() {
      this.loading = true
      this.error = null

      try {
        const response = await this.$angularWorld.adminEntitiesApi.listSupergroups({
          with_children: true
        })
        this.supergroups = response.data
      } catch (err) {
        console.error('Failed to fetch supergroups:', err)
        this.error = 'Failed to load supergroups. Please try again.'
      } finally {
        this.loading = false
      }
    },
    editSupergroup(supergroup) {
      this.currentSupergroup = { ...supergroup }
      this.showEditModal = true
    },
    confirmDelete(supergroup) {
      this.currentSupergroup = supergroup
      this.showDeleteModal = true
    },
    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.currentSupergroup = null
    },
    async saveSupergroup(supergroup) {
      try {
        if (this.showEditModal) {
          await this.$angularWorld.adminEntitiesApi.updateSupergroup(supergroup)
        } else {
          await this.$angularWorld.adminEntitiesApi.createSupergroup(supergroup)
        }

        this.closeModal()
        await this.fetchSupergroups()
      } catch (err) {
        console.error('Failed to save supergroup:', err)
        this.error = `Failed to save supergroup. Please try again.`
      }
    },
    findSupergroupAndItsAncestors(uuid) {
      const supergroupMap = new Map(this.supergroups.map(sg => [sg.uuid, sg]))
      const ancestors = []
      let current = supergroupMap.get(uuid)
      while (current) {
        ancestors.unshift(current.uuid)
        current = supergroupMap.get(current.parent_id)
      }
      return ancestors
    },

    findComponentByUuid(components, uuid) {
      if (!components) {
        return null
      }
      for (const component of components) {
        if (component.supergroup.uuid === uuid) {
          return component
        }
        // Check recursively in children that are now rendered because parent is expanded
        if (component.expanded && component.$refs.supergroupListItems) {
          const found = this.findComponentByUuid(component.$refs.supergroupListItems, uuid)
          if (found) {
            return found
          }
        }
      }
      return null
    },
      selectSupergroup(supergroup) {
        this.selectedSupergroup = supergroup;
        this.isUserPanelOpen = true;
      },
      closeUserPanel() {
        this.isUserPanelOpen = false;
      },
    async deleteSupergroup() {
      if (!this.currentSupergroup) return

      try {
        await this.$angularWorld.adminEntitiesApi.deleteSupergroup(this.currentSupergroup.uuid)
        this.showDeleteModal = false
        this.currentSupergroup = null
        await this.fetchSupergroups()
      } catch (err) {
        console.error('Failed to delete supergroup:', err)
        this.error = `Failed to delete supergroup. Please try again.`
        this.showDeleteModal = false
      }
    }
  }
}
</script>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #3b82f6;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.supergroup-tree {
  margin-left: -1rem;
}
.odd {
  background: rgb(245, 245, 245);
}
.even {
  background: white;
}
.mb-3 {
  margin-bottom: 12px;
}
.mb-4 {
  margin-bottom: 16px;
}
</style>
